<template>
  <BaseModal
    :model-value="isOpen"
    @update:model-value="(v: boolean) => { isOpen = v }"
    title="Open a Ticket"
    size="large"
    :before-dismiss="dirtyClose.beforeDismiss"
  >
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <BaseSelect
        v-model="formData.category"
        label="Category"
        :options="categoryOptions"
        placeholder="Select category"
      />

      <BaseSelect
        v-model="formData.priority"
        label="Priority"
        :options="priorityOptions"
        placeholder="Select priority"
      />

      <BaseInput
        v-model="formData.title"
        label="Title"
        placeholder="Brief description of the issue"
        required
        :error-text="errors.title"
      />

      <BaseTextArea
        v-model="formData.description"
        label="Description"
        placeholder="Describe the issue in detail"
        required
        :rows="4"
        :error-text="errors.description"
      />

      <div class="grid grid-cols-2 gap-4">
        <BaseInput
          v-model="formData.browser"
          label="Browser"
          placeholder="e.g., Chrome 120, Firefox 121"
        />

        <BaseInput
          v-model="formData.os"
          label="Operating System"
          placeholder="e.g., macOS 14, Windows 11"
        />
      </div>

      <BaseTextArea
        v-model="formData.steps_to_reproduce"
        label="Steps to Reproduce"
        placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
        :rows="3"
        help-text="List the steps that lead to the issue"
      />

      <BaseTextArea
        v-model="formData.expected_behavior"
        label="Expected Behavior"
        placeholder="What should happen?"
        :rows="2"
      />

      <BaseTextArea
        v-model="formData.actual_behavior"
        label="Actual Behavior"
        placeholder="What actually happens?"
        :rows="2"
      />

      <BaseInput
        v-model="formData.page_url"
        label="Page URL"
        placeholder="Current page URL"
        help-text="The URL where this bug was reported from"
      />

      <div>
        <BaseMultiFileUpload
          ref="fileUploadRef"
          :model-value="[]"
          label="Attachments (Optional)"
          accepted-types="image/*,.pdf,.doc,.docx,.csv"
          :max-files="100"
          :required="false"
          :error-text="errors.attachments"
          @files-selected="handleFilesSelected"
          @file-removed="handleFileRemoved"
        />
        <p class="mt-1 text-xs text-text-muted">
          Accepted formats: Images (JPG, PNG, GIF), PDF, DOC, DOCX, CSV. Max: 10MB per file, up to 100 files
        </p>
      </div>
    </form>

    <template #footer="{ requestClose }">
      <div class="flex items-center gap-2" style="width: 100%; margin-right: auto;">
        <BaseButton
          variant="secondary"
          @click="handleClearForm"
          :disabled="submitting"
        >
          Clear Form
        </BaseButton>
        <div class="flex-1"></div>
        <div class="flex gap-2">
          <BaseButton
            variant="secondary"
            @click="() => { if (!submitting) requestClose() }"
            :disabled="submitting"
          >
            Cancel
          </BaseButton>
          <BaseButton
            @click="handleSubmit"
            variant="primary"
            :disabled="submitting"
            :loading="submitting"
          >
            Submit Bug Report
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>

  <BaseConfirmDialog
    v-model="dirtyClose.showCloseConfirm"
    :title="dirtyClose.confirmDialogTitle"
    :message="dirtyClose.confirmDialogMessage"
    :confirm-label="dirtyClose.confirmDialogConfirmLabel"
    :cancel-label="dirtyClose.confirmDialogCancelLabel"
    :confirm-variant="dirtyClose.confirmDialogVariant"
    :layout="dirtyClose.confirmDialogLayout"
    @confirm="dirtyClose.onCloseConfirmConfirm"
    @cancel="dirtyClose.onCloseConfirmCancel"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import BaseModal from '@/components/feedback/BaseModal.vue'
import BaseConfirmDialog from '@/components/feedback/BaseConfirmDialog.vue'
import { useModalOpenBaselineGuard } from '@/composables/useModalOpenBaselineGuard'
import BaseInput from '@/components/forms/BaseInput.vue'
import BaseTextArea from '@/components/forms/BaseTextArea.vue'
import BaseSelect from '@/components/forms/BaseSelect.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseMultiFileUpload from '@/components/forms/BaseMultiFileUpload.vue'
import { bugReportService, type CreateBugReportData } from '@/services/bugReportService'
import { toast } from '@/services/toastService'
import { getUserAgentInfo } from '@/utils/userAgent'

const route = useRoute()

const STORAGE_KEY = 'bugReportFormData'

interface Props {
  modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  submitted: []
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

function bugReportFormSnapshot() {
  return {
    title: formData.title,
    description: formData.description,
    priority: formData.priority,
    category: formData.category,
    browser: formData.browser,
    os: formData.os,
    steps_to_reproduce: formData.steps_to_reproduce,
    expected_behavior: formData.expected_behavior,
    actual_behavior: formData.actual_behavior,
    page_url: formData.page_url,
    files: selectedFiles.value.map((f) => f.name).sort().join('|')
  }
}

const dirtyClose = useModalOpenBaselineGuard(() => props.modelValue, bugReportFormSnapshot, 'preserve')

const submitting = ref(false)
const selectedFiles = ref<File[]>([])
const fileUploadRef = ref<InstanceType<typeof BaseMultiFileUpload> | null>(null)
const isLoadingFormData = ref(false)

const formData = reactive<CreateBugReportData>({
  title: '',
  description: '',
  priority: 'medium',
  category: 'bug',
  browser: '',
  os: '',
  steps_to_reproduce: '',
  expected_behavior: '',
  actual_behavior: '',
  page_url: ''
})

const errors = reactive<Record<string, string>>({
  title: '',
  description: '',
  attachments: ''
})

// Load form data from localStorage
const loadFormData = () => {
  try {
    isLoadingFormData.value = true
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      Object.assign(formData, {
        title: parsed.title || '',
        description: parsed.description || '',
        priority: parsed.priority || 'medium',
        category: parsed.category || 'bug',
        browser: parsed.browser || '',
        os: parsed.os || '',
        steps_to_reproduce: parsed.steps_to_reproduce || '',
        expected_behavior: parsed.expected_behavior || '',
        actual_behavior: parsed.actual_behavior || '',
        page_url: parsed.page_url || ''
      })
    }
  } catch (error) {
    console.error('Error loading form data from localStorage:', error)
  } finally {
    isLoadingFormData.value = false
  }
}

// Save form data to localStorage
const saveFormData = () => {
  // Don't save while loading to avoid overwriting with stale data
  if (isLoadingFormData.value) return
  
  try {
    const dataToSave = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      category: formData.category,
      browser: formData.browser,
      os: formData.os,
      steps_to_reproduce: formData.steps_to_reproduce,
      expected_behavior: formData.expected_behavior,
      actual_behavior: formData.actual_behavior,
      page_url: formData.page_url
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
  } catch (error) {
    console.error('Error saving form data to localStorage:', error)
  }
}

// Watch form data changes and save to localStorage
watch(() => formData, () => {
  saveFormData()
}, { deep: true })

const categoryOptions = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'other', label: 'Other' }
]

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
]

// Auto-fill page URL when modal opens and load saved form data
watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    // Load saved form data from localStorage
    loadFormData()
    
    // Auto-fill page URL when modal opens (only if empty, preserve existing URL)
    if (!formData.page_url) {
      const baseUrl = window.location.origin
      const fullPath = route.fullPath
      formData.page_url = `${baseUrl}${fullPath}`
    }
    
    // Auto-fill browser and OS (only if empty, to avoid overwriting user input or saved data)
    if (!formData.browser || !formData.os) {
      try {
        const { browser, os } = await getUserAgentInfo()
        
        // Only set if field is currently empty
        if (!formData.browser && browser) {
          formData.browser = browser
        }
        if (!formData.os && os) {
          formData.os = os
        }
      } catch (error) {
        // Silently fail - detection is best effort
        console.debug('Failed to detect browser/OS:', error)
      }
    }
  }
})

const handleFilesSelected = (files: File[]) => {
  // Validate files
  const maxSize = 10 * 1024 * 1024 // 10MB
  const validFiles: File[] = []
  
  for (const file of files) {
    if (file.size > maxSize) {
      toast.error(`File "${file.name}" exceeds 10MB limit`)
      continue
    }
    validFiles.push(file)
  }
  
  // Add to selected files (avoid duplicates)
  const existingNames = new Set(selectedFiles.value.map(f => f.name))
  const newFiles = validFiles.filter(f => !existingNames.has(f.name))
  selectedFiles.value = [...selectedFiles.value, ...newFiles]
  
  // Check max files limit
  if (selectedFiles.value.length > 100) {
    toast.error('Maximum 100 files allowed')
    selectedFiles.value = selectedFiles.value.slice(0, 100)
  }
}

const handleFileRemoved = (index: number) => {
  selectedFiles.value.splice(index, 1)
}

const resetForm = () => {
  formData.title = ''
  formData.description = ''
  formData.priority = 'medium'
  formData.category = 'bug'
  formData.browser = ''
  formData.os = ''
  formData.steps_to_reproduce = ''
  formData.expected_behavior = ''
  formData.actual_behavior = ''
  formData.page_url = ''
  errors.title = ''
  errors.description = ''
  errors.attachments = ''
  selectedFiles.value = []
  // Clear localStorage
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing form data from localStorage:', error)
  }
}

const handleClearForm = () => {
  if (!submitting.value) {
    resetForm()
    // Update page URL to current page
    const baseUrl = window.location.origin
    const fullPath = route.fullPath
    formData.page_url = `${baseUrl}${fullPath}`
  }
}

const handleSubmit = async () => {
  // Reset errors
  errors.title = ''
  errors.description = ''

  // Validate
  if (!formData.title?.trim()) {
    errors.title = 'Title is required'
    return
  }

  if (!formData.description?.trim()) {
    errors.description = 'Description is required'
    return
  }

  submitting.value = true

  try {
    // Create FormData if attachments are present, otherwise use JSON
    if (selectedFiles.value.length > 0) {
      const formDataToSend = new FormData()
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        const value = formData[key as keyof CreateBugReportData]
        if (value !== null && value !== undefined && value !== '') {
          formDataToSend.append(key, value as string)
        }
      })
      
      // Add attachments as array
      selectedFiles.value.forEach((file) => {
        formDataToSend.append('attachments[]', file)
      })
      
      await bugReportService.createBugReport(formDataToSend)
    } else {
      // Send as JSON if no attachments
      await bugReportService.createBugReport(formData)
    }
    
    toast.success('Bug report submitted successfully!')
    emit('submitted')
    // Reset form after successful submission
    resetForm()
    isOpen.value = false
  } catch (error: any) {
    console.error('Error submitting bug report:', error)
    
    if (error.response?.data?.errors) {
      const apiErrors = error.response.data.errors
      if (apiErrors.title) errors.title = apiErrors.title[0]
      if (apiErrors.description) errors.description = apiErrors.description[0]
      if (apiErrors.attachments) {
        errors.attachments = Array.isArray(apiErrors.attachments) 
          ? apiErrors.attachments[0] 
          : apiErrors.attachments
      }
    } else {
      toast.error(error.response?.data?.message || 'Failed to submit bug report')
    }
  } finally {
    submitting.value = false
  }
}
</script>

